import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import ormStationsService from "../../hooks/api/stations";
import apiService from "../../hooks/api/axios";

export interface DateRange {
  start: string | null;
  end: string | null;
}

export interface TimeRange {
  start: string | null;
  end: string | null;
}

export interface Sid {
  token?: string;
  expire?: string;
}

export interface UserSessionState {
  reservationDate: DateRange | null;
  reservationTime: TimeRange | null;
  selectedStation: string | null;
  selectedStationEnd: string | null;
  stationtype: string | null;
  carcategoryselect: any | null;
  stationBranch: string | null;
  startDate: string | null;
  endDate: string | null;
  startTime: string | null;
  endTime: string | null;
  cargroup: string | null;
  prices: any;
  pricesLoading: Record<string, boolean>;
  sid: any;
  rate_code: string;
  offer: string;
}

const initialState: UserSessionState = {
  reservationDate: { start: null, end: null },
  reservationTime: { start: null, end: null },
  selectedStation: null,
  selectedStationEnd: null,
  stationtype: "RENT",
  carcategoryselect: null,
  stationBranch: "RENT",
  startDate: "",
  endDate: "",
  startTime: "",
  endTime: "",
  cargroup: null,
  prices: {} as Record<string, any>,
  pricesLoading: {} as Record<string, boolean>,
  sid: {},
  rate_code: "",
  offer: "",
};

// Versuche Session aus sessionStorage zu laden
const loadSessionFromStorage = (): UserSessionState => {
  try {
    const stored = sessionStorage.getItem(
      `ORM${process.env.NODE_ENV === "development" ? "_" + process.env.REACT_APP_DEPLOYCUSTOMERID : ""}_userSession`,
    );
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("Error loading session from storage:", error);
  }
  return initialState;
};

const userSessionSlice = createSlice({
  name: "userSession",
  initialState: loadSessionFromStorage(),
  reducers: {
    setReservationDate: (state, action: PayloadAction<DateRange | null>) => {
      state.reservationDate = action.payload;
    },
    setReservationTime: (state, action: PayloadAction<TimeRange | null>) => {
      state.reservationTime = action.payload;
    },
    setSelectedStation: (state, action: PayloadAction<any>) => {
      if (action.payload.type === "start") {
        state.selectedStation = action.payload.stationId;
      } else {
        state.selectedStationEnd = action.payload.stationId;
      }
      ormStationsService.fetchOpeningTimes(state);
      state.prices = {};
      state.pricesLoading = {};
    },
    setStationType: (state, action: PayloadAction<string | null>) => {
      state.stationtype = action.payload;
    },
    setCarCategorySelect: (state, action: PayloadAction<any>) => {
      state.carcategoryselect = action.payload;
    },
    setStationBranch: (state, action: PayloadAction<string | null>) => {
      state.stationBranch = action.payload;
    },
    // Generische Setter-Funktion (wie dein setUserSession)
    setUserSessionField: (
      state,
      action: PayloadAction<{ key: keyof UserSessionState; value: any }>,
    ) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    setStartDate: (state, action: PayloadAction<any>) => {
      state.startDate = action.payload;
      state.reservationDate!.start = action.payload;

      // ormStationsService.fetchOpeningTimes(state);
    },
    setEndDate: (state, action: PayloadAction<any>) => {
      state.endDate = action.payload;
      state.reservationDate!.end = action.payload;
      // ormStationsService.fetchOpeningTimes(state);
    },
    setStartTime: (state, action: PayloadAction<any>) => {
      state.startTime = action.payload;
      state.reservationTime!.start = action.payload;
      // ormStationsService.fetchOpeningTimes(state);
    },
    setEndTime: (state, action: PayloadAction<any>) => {
      state.endTime = action.payload;
      state.reservationTime!.end = action.payload;
      // ormStationsService.fetchOpeningTimes(state);
    },
    setSelectedVehicle: (
      state,
      action: PayloadAction<{
        vehicleId: string | null;
        categories: string[] | null;
      }>,
    ) => {
      if (!action.payload.categories) {
        sessionStorage.removeItem(
          `ORM${process.env.NODE_ENV === "development" ? "_" + process.env.REACT_APP_DEPLOYCUSTOMERID : ""}_selectedCarCategory`,
        );
        return;
      }
      state.cargroup = action.payload.vehicleId;
      try {
        const categoryObject = action.payload.categories.reduce(
          (acc, category) => {
            acc[category] = true;
            return acc;
          },
          {} as Record<string, boolean>,
        );

        sessionStorage.setItem(
          `ORM${process.env.NODE_ENV === "development" ? "_" + process.env.REACT_APP_DEPLOYCUSTOMERID : ""}_selectedCarCategory`,
          JSON.stringify(categoryObject),
        );
      } catch (error) {
        console.error("Error storing selected car category:", error);
      }
    },
    setSid: (state) => {
      const loginsession = localStorage.getItem(
        `ORM${process.env.NODE_ENV === "development" ? "_" + process.env.REACT_APP_DEPLOYCUSTOMERID : ""}_LoginSession`,
      ) as string;
      state.sid = JSON.parse(loginsession) || {};
      apiService.setToken(state.sid);
    },
    setOfferSelected: (state, action: PayloadAction<any>) => {
      if (action.payload == null) {
        sessionStorage.removeItem(
          `ORM${process.env.NODE_ENV === "development" ? "_" + process.env.REACT_APP_DEPLOYCUSTOMERID : ""}_offer_selected`,
        );
        return;
      }

      sessionStorage.setItem(
        `ORM${process.env.NODE_ENV === "development" ? "_" + process.env.REACT_APP_DEPLOYCUSTOMERID : ""}_offer_selected`,
        JSON.stringify(action.payload),
      );
      state.offer = action.payload;
      state.rate_code = action.payload!.rate_code!;
    },
    resetUserSession: () => initialState,
    setPriceLoading: (state, action: PayloadAction<string>) => {
      state.pricesLoading[action.payload] = true;
    },
    setPriceSuccess: (
      state,
      action: PayloadAction<{ id: string; data: any }>,
    ) => {
      // We only extract the specific value we need
      const finalPrice = action.payload.data?.total_price_main_rates || "";
      const finalUnit = action.payload.data?.main_rates[0].unit || "";

      // If it exists, store it; otherwise store null to mark it as "no price available"
      state.prices[action.payload.id] = {
        price: finalPrice,
        unit: finalUnit,
      };
      state.pricesLoading[action.payload.id] = false;
    },
    setPriceError: (state, action: PayloadAction<string>) => {
      state.pricesLoading[action.payload] = false;
    },
  },
});

export const {
  setReservationDate,
  setReservationTime,
  setSelectedStation,
  setStationType,
  setCarCategorySelect,
  setStationBranch,
  setUserSessionField,
  setStartDate,
  setEndDate,
  setStartTime,
  setEndTime,
  setSelectedVehicle,
  resetUserSession,
  setPriceLoading,
  setPriceSuccess,
  setPriceError,
  setSid,
  setOfferSelected,
} = userSessionSlice.actions;

export default userSessionSlice.reducer;
